/**
 * ProjectInvoicesBox.jsx - a project's invoices in the record editor's
 * Invoicing section (main tab, 10/11/26).
 *
 * Invoicing is the invoice ledger now (server/lib/invoiceLedger.js): each
 * invoice is its own row, and the project's Invoiced, Invoice #'s, Latest
 * Invoice Date and Leftover are worked out from it - shown read only above
 * this box. "Add an invoice" here writes one ledger row AT ONCE (POST
 * /projects/:id/invoices, with Projects / Opportunities Edit), not on the
 * editor's Save, and the read-only fields follow. Correcting, voiding and
 * payments are on the Finance Invoice tab (its link shows to those who can
 * see that tab). With Finance Invoice Edit, clicking an invoice here opens the
 * same invoice pop-up as that tab, so it is corrected without leaving the
 * project (T-0008, Mohammed). The GLOBAL KSA tab keeps its typed fields and
 * AddInvoiceRow.
 *
 *   projectId   the saved record's id; null while a new record is not saved yet
 *   base        the API base (useApiBase: '/api' here)
 *   editable    the editor's rights (no Add without them)
 *   nextNumber  () -> the next invoice number in the company's series
 *   onAdded     (project) -> the project's new invoicing fields, camelCase
 *   tabLink     true when the person can open the Finance Invoice tab
 *   ledgerEdit  true with Finance Invoice Edit: invoices open in the invoice pop-up
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { INPUT, LABEL } from '../forms/formStyles';
import { fmtMoney, fmtDate } from '../../utils/format';
import { latestReads, useLiveRefresh } from '../../live/useLive';
import InvoiceModal from '../financeInvoices/InvoiceModal';
import { listProjects } from '../financeInvoices/financeInvoicesApi';

const GREEN = '#1B5E20';
const MUTED = '#6B7168';
const money = (n) => (n === null || n === undefined ? 'amount to enter' : fmtMoney(Number(n)));

export default function ProjectInvoicesBox({ projectId, base, editable, nextNumber, onAdded, tabLink, ledgerEdit = false }) {
  const today = new Date().toLocaleDateString('en-CA'); // the local day, YYYY-MM-DD
  const [inv, setInv] = useState(() => ({ number: (nextNumber && nextNumber()) || '', amount: '', date: today }));
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [list, setList] = useState(null);
  const [reads] = useState(latestReads);
  const [open, setOpen] = useState(null); // the invoice id in the pop-up
  const [projects, setProjects] = useState(null); // the pop-up's project picker
  // the editor makes a new onAdded every render; a ref keeps load() stable
  const added = useRef(onAdded);
  added.current = onAdded;

  const load = useCallback(
    (follow = false) => {
      if (!projectId) return Promise.resolve();
      const t = reads.start();
      return Promise.resolve()
        .then(() => fetch(`${base}/projects/${encodeURIComponent(projectId)}/invoices`))
        .then(async (r) => {
          const body = r && r.ok ? await r.json() : null;
          if (body && Array.isArray(body.invoices) && reads.show(t)) setList(body.invoices);
          // after a correction in the pop-up the read-only Invoiced fields follow
          if (follow === true && body && body.project && added.current) added.current(body.project);
        })
        .catch(() => {});
    },
    [projectId, base, reads]
  );
  const openInvoice = (id) => {
    setOpen(id);
    if (!projects)
      listProjects()
        .then((res) => setProjects(res && res.ok && Array.isArray(res.body) ? res.body : []))
        .catch(() => setProjects([]));
  };
  useEffect(() => {
    load();
  }, [load]);
  // someone else's invoice on this project (the editor or the Finance Invoice tab)
  useLiveRefresh(['projects', 'finance-invoices'], load, {
    match: (e) =>
      e.kind === 'finance-invoices' ||
      (String(e.id) === String(projectId) &&
        (e.sub === 'invoices' || (e.fields || []).includes('invoiced_already') || (e.fields || []).includes('invoice_numbers'))),
  });

  const add = async (allowDuplicate = false) => {
    if (busy) return;
    const amount = String(inv.amount).trim();
    if (!amount || !Number.isFinite(Number(amount))) {
      setMsg({ bad: true, text: 'Enter the invoice amount.' });
      return;
    }
    if (!inv.date) {
      setMsg({ bad: true, text: 'Enter the invoice date.' });
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      const r = await fetch(`${base}/projects/${encodeURIComponent(projectId)}/invoices`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          invoice_number: inv.number,
          amount,
          invoice_date: inv.date,
          ...(allowDuplicate ? { allow_duplicate_number: true } : {}),
        }),
      });
      const body = await r.json().catch(() => ({}));
      if (r.status === 409 && body.code === 'invoice_number_taken') {
        setMsg({ bad: true, text: `${body.error} Add it again anyway?`, dup: true });
        return;
      }
      if (!r.ok) {
        setMsg({ bad: true, text: body.error || 'Could not add the invoice - try again.' });
        return;
      }
      if (onAdded && body.project) onAdded(body.project);
      setMsg({
        bad: false,
        text: `Added${inv.number ? ` invoice ${inv.number}` : ' the invoice'} for ${fmtMoney(Number(amount))}. Saved to the invoice ledger.`,
      });
      const n = String(inv.number || '');
      // the next number in the same zero-padded series, ready for the next invoice
      const next = /^\d+$/.test(n) ? String(parseInt(n, 10) + 1).padStart(n.length, '0') : '';
      setInv({ number: next, amount: '', date: today });
      load();
    } catch {
      setMsg({ bad: true, text: 'Could not add the invoice - try again.' });
    } finally {
      setBusy(false);
    }
  };

  const box = { ...INPUT, padding: '7px 10px' };
  const review = (list || []).filter((i) => i.needs_review);
  return (
    <div style={{ marginTop: 10, padding: '12px 14px', background: '#F6F9F3', border: '1px solid #E0EAD8', borderRadius: 8 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 10.5, fontWeight: 700, color: GREEN }}>Add an invoice</div>
        <div style={{ fontSize: 10, color: MUTED }}>Invoiced, Invoice #&apos;s and Latest Invoice Date follow the invoice ledger.</div>
        {tabLink && projectId && (
          <a
            href={`#/finance-invoices?project=${encodeURIComponent(projectId)}`}
            style={{ fontSize: 10.5, fontWeight: 700, color: GREEN, marginLeft: 'auto' }}
          >
            Correct, void or record payments on the Finance Invoice tab
          </a>
        )}
      </div>
      {!projectId ? (
        <div style={{ fontSize: 11, color: MUTED }}>Save the project first, then add its invoices here.</div>
      ) : (
        <>
          {list && list.length > 0 && (
            <div aria-label="This project's invoices" style={{ marginBottom: 10, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {list.map((i) => {
                const Tag = ledgerEdit ? 'button' : 'span';
                return (
                  <Tag
                    key={i.id}
                    {...(ledgerEdit
                      ? { type: 'button', onClick: () => openInvoice(i.id), 'aria-label': `Edit invoice ${i.invoice_number || ''}`.trim() }
                      : {})}
                    title={ledgerEdit ? 'Edit this invoice' : i.needs_review ? i.review_note || 'Needs review' : undefined}
                    style={{
                      fontSize: 10.5,
                      padding: '3px 9px',
                      borderRadius: 12,
                      background: '#fff',
                      fontVariantNumeric: 'tabular-nums',
                      border: `1px solid ${i.needs_review ? '#F2C66D' : '#DCE7D6'}`,
                      color: '#1F2328',
                      fontFamily: 'inherit',
                      cursor: ledgerEdit ? 'pointer' : undefined,
                    }}
                  >
                    <strong>{i.kind === 'opening' ? 'Opening balance' : i.invoice_number || 'No #'}</strong>
                    {' · '}
                    {money(i.amount)}
                    {i.invoice_date ? ` · ${fmtDate(i.invoice_date)}` : ''}
                    {i.needs_review && <span style={{ color: '#92400E', fontWeight: 700 }}> · needs review</span>}
                    {ledgerEdit && <span style={{ color: GREEN, fontWeight: 700 }}> · Edit</span>}
                  </Tag>
                );
              })}
            </div>
          )}
          {review.length > 0 && (
            <div style={{ fontSize: 10.5, color: '#92400E', marginBottom: 8 }}>
              {review.length === 1 ? '1 invoice was' : `${review.length} invoices were`} carried over from before the ledger and need their
              amount or date checked on the Finance Invoice tab.
            </div>
          )}
          {editable && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <label style={{ flex: '1 1 110px' }}>
                <span style={LABEL}>Invoice #</span>
                <input
                  aria-label="New invoice number"
                  style={box}
                  value={inv.number}
                  onChange={(e) => setInv((v) => ({ ...v, number: e.target.value }))}
                  placeholder="e.g. 00617"
                />
              </label>
              <label style={{ flex: '1 1 110px' }}>
                <span style={LABEL}>Amount ($)</span>
                <input
                  aria-label="New invoice amount"
                  style={box}
                  value={inv.amount}
                  inputMode="decimal"
                  onChange={(e) => setInv((v) => ({ ...v, amount: e.target.value.replace(/[^0-9.-]/g, '') }))}
                  placeholder="0.00"
                />
              </label>
              <label style={{ flex: '1 1 130px' }}>
                <span style={LABEL}>Date</span>
                <input
                  aria-label="New invoice date"
                  type="date"
                  style={box}
                  value={inv.date}
                  onChange={(e) => setInv((v) => ({ ...v, date: e.target.value }))}
                />
              </label>
              <button
                type="button"
                disabled={busy}
                onClick={() => add(false)}
                style={{
                  background: GREEN,
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  padding: '8px 16px',
                  fontWeight: 700,
                  fontSize: 11,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  opacity: busy ? 0.6 : 1,
                }}
              >
                Add
              </button>
            </div>
          )}
        </>
      )}
      {msg && (
        <div role={msg.bad ? 'alert' : 'status'} style={{ fontSize: 10.5, marginTop: 8, color: msg.bad ? '#B71C1C' : GREEN }}>
          {msg.text}
          {msg.dup && (
            <button
              type="button"
              onClick={() => add(true)}
              style={{
                marginLeft: 8,
                fontSize: 10.5,
                fontWeight: 700,
                color: GREEN,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                textDecoration: 'underline',
                fontFamily: 'inherit',
              }}
            >
              Add it anyway
            </button>
          )}
        </div>
      )}
      {open && (
        <InvoiceModal
          key={open}
          invoiceId={open}
          projects={projects || []}
          editable={ledgerEdit}
          onClose={() => setOpen(null)}
          onChanged={() => load(true)}
        />
      )}
    </div>
  );
}
